import {useEffect, useState} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';
import {AutoGeneratedBadge} from '@yourcoach/shared/components/AutoGeneratedBadge';
import {Modal} from '@yourcoach/shared/components/Modal';
import {useInjection, withModuleLoaderHOC} from '@yourcoach/shared/IoC';
import {coachCromsModule} from '@yourcoach/shared/modules/croms/coach/IoC';
import {
  CromQuestion,
  ValidationMessage,
} from '@yourcoach/shared/modules/croms/coach/ui';
import {isCancelCommand} from '@yourcoach/shared/services/entity-action/utils';
import {LoadingSpinner} from '@yourcoach/shared/uikit/LoadingSpinner';
import {Text} from '@yourcoach/shared/uikit/Text';
import {logger} from '@yourcoach/shared/utils/logger';

import * as S from './styles';

type Props = {
  cromId: string;
  onSolved?: () => Promise<void>;
  placeForLogging?: string;
};

export const CromSolvingModal = NiceModal.create<Props>(
  withModuleLoaderHOC(
    {
      modules: [coachCromsModule],
    },
    ({cromId, onSolved, placeForLogging}) => {
      const {t} = useI18n();
      const modal = useModal();
      const store = useInjection(
        coachCromsModule.exports.CromSolvingStore,
        coachCromsModule.id,
      );
      const [isLoading, setIsLoading] = useState(false);

      useEffect(() => {
        logger.event('crom_view');
      }, []);

      useEffect(() => {
        store.fetch(cromId);
      }, [store, cromId]);

      const onSubmit = async () => {
        logger.event(
          'crom_submit_tap',
          placeForLogging ? {place: placeForLogging} : {},
        );

        const result = await store.sendAnswers();

        if (!isCancelCommand(result)) {
          if (onSolved) {
            setIsLoading(true);
            await onSolved();
            setIsLoading(false);
          }

          modal.hide();
        }
      };

      const onRequestClose = () => {
        logger.event('crom_close_tap');

        modal.hide();
      };

      const renderFooter = () => (
        <S.Footer>
          <Text color="text3" weight="semibold">
            {`*${t('shared:required_question')}`}
          </Text>
          <Observer>
            {() => (
              <S.SubmitButton
                disabled={store.isFetching || !store.hasSelectedOptions}
                onClick={onSubmit}
              >
                {t('shared:submit')}
              </S.SubmitButton>
            )}
          </Observer>
        </S.Footer>
      );

      return (
        <Modal
          isFullWidth
          isOpen={modal.visible}
          maxWidth="780px"
          onAfterClose={modal.remove}
          onRequestClose={onRequestClose}
          renderFooter={renderFooter}
          title={`${t('shared:please_answer_questions_post_live_session')}:`}
        >
          <S.Container>
            <Observer>
              {() =>
                !store.isFetching ? (
                  <S.ClientNameContainer>
                    <S.ClientName>
                      {`${t('shared:client_name')}: `}
                      <S.ClientName weight="semibold">
                        {store.clientName}
                      </S.ClientName>
                    </S.ClientName>
                  </S.ClientNameContainer>
                ) : null
              }
            </Observer>
            <Observer>
              {() => (
                <S.QuestionsList>
                  <S.Header>
                    {store.validationMessage ? (
                      <ValidationMessage message={store.validationMessage} />
                    ) : null}

                    {store.isAiGenerated ? <AutoGeneratedBadge /> : null}
                  </S.Header>
                  {store.questions.map((question, index) => (
                    <CromQuestion
                      answer={store.getAnswerByQuestionId(question.id)}
                      hasError={store.questionWithErrorIds.has(question.id)}
                      index={index}
                      key={question.id}
                      onAnswerChange={store.setAnswer}
                      question={question}
                    />
                  ))}
                </S.QuestionsList>
              )}
            </Observer>

            <Observer>
              {() =>
                store.isFetching || store.isSendingAnswers || isLoading ? (
                  <S.Overlay>
                    <LoadingSpinner />
                  </S.Overlay>
                ) : null
              }
            </Observer>
          </S.Container>
        </Modal>
      );
    },
  ),
);
